import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { SnackbarProvider } from 'notistack';

import LoginPage from './components/LoginPage';
import AdminLayout from './components/AdminLayout';
import Dashboard from './components/Dashboard';
import PaymentAudit from './components/PaymentAudit';
import SecurityAlerts from './components/SecurityAlerts';
import UserManagement from './components/UserManagement';
import EmailManagement from './components/EmailManagement';
import GalleryManagement from './components/GalleryManagement';
import NotificationManagement from './components/NotificationManagement';
import Operations from './components/Operations';
import PaystackCallback from './components/PaystackCallback';

import SystemSettings from './components/SystemSettings';
import {
  clearAdminCsrfToken,
  configureAdminApi,
  setAdminCsrfToken,
  setAdminActionConfirmationHandler,
  setAdminSessionExpiredHandler,
} from './services/adminApi';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// SECURITY: Fail fast if no backend URL is configured
if (!API_BASE_URL) {
  console.error('REACT_APP_API_BASE_URL environment variable is required');
  throw new Error('Backend URL not configured. Please set REACT_APP_API_BASE_URL environment variable.');
}

configureAdminApi(API_BASE_URL);

function App() {
  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  useEffect(() => {
    setAdminActionConfirmationHandler((action) => new Promise((resolve) => {
      setPendingConfirmation({ action, resolve });
    }));
    return () => setAdminActionConfirmationHandler(null);
  }, []);

  useEffect(() => {
    setAdminSessionExpiredHandler(() => {
      clearAdminCsrfToken();
      setSession(null);
    });
    return () => setAdminSessionExpiredHandler(null);
  }, []);
  
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/v1/admin/session`)
      .then((response) => {
        setAdminCsrfToken(response.data.csrfToken);
        setSession(response.data);
      })
      .catch(() => {
        clearAdminCsrfToken();
        setSession(null);
      })
      .finally(() => setCheckingSession(false));
  }, []);

  const handleLogin = (newSession) => {
    setAdminCsrfToken(newSession.csrfToken);
    setSession(newSession);
  };

  const handleLogout = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/admin/session`);
    } finally {
      clearAdminCsrfToken();
      setSession(null);
    }
  };

  const finishConfirmation = (confirmed) => {
    pendingConfirmation?.resolve(confirmed);
    setPendingConfirmation(null);
  };

  if (checkingSession) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
        <Router>
        <Box sx={{ height: '100vh' }}>
          <Routes>
            {/* Public Paystack callback route - no authentication required */}
            <Route path="/paystack-callback" element={<PaystackCallback />} />
            
            <Route 
              path="/login" 
              element={
                !session ? (
                  <LoginPage onLogin={handleLogin} />
                ) : (
                  <Navigate to="/admin" replace />
                )
              } 
            />
            <Route 
              path="/admin/*" 
              element={
                session ? (
                  <AdminLayout onLogout={handleLogout}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/payments" element={<PaymentAudit />} />
                      <Route path="/security" element={<SecurityAlerts />} />
                      <Route path="/users" element={<UserManagement />} />
                      <Route path="/emails" element={<EmailManagement />} />
                      <Route path="/gallery" element={<GalleryManagement />} />
                      <Route path="/notifications" element={<NotificationManagement />} />
                      <Route path="/operations" element={<Operations />} />
                      <Route path="/settings" element={<SystemSettings />} />
                    </Routes>
                  </AdminLayout>
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            <Route path="/" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Box>
      </Router>
      </SnackbarProvider>
      <Dialog
        open={Boolean(pendingConfirmation)}
        onClose={() => finishConfirmation(false)}
        aria-labelledby="admin-action-confirmation-title"
      >
        <DialogTitle id="admin-action-confirmation-title">
          Confirm Sensitive Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action can change important system or user data. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => finishConfirmation(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => finishConfirmation(true)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default App;
